import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';

export interface SelectOption<T extends string = string> {
  value: T;
  label: ReactNode;
  /** Search/typeahead text. */
  text?: string;
  description?: ReactNode;
  icon?: ReactNode;
  group?: string;
  disabled?: boolean;
}

interface Position {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  above: boolean;
}

const POP_MAX = 300;

function plain(option: SelectOption): string {
  if (option.text) return option.text;
  return typeof option.label === 'string' ? option.label : option.value;
}

export function Select<T extends string = string>({
  value,
  onChange,
  options,
  placeholder = 'Pilih…',
  disabled = false,
  size = 'md',
  searchable,
  id,
  className,
  style,
  'aria-label': ariaLabel,
}: {
  value: T | '' | null | undefined;
  onChange: (value: T) => void;
  options: ReadonlyArray<SelectOption<T>>;
  placeholder?: ReactNode;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Default: over 8 options. */
  searchable?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<Position | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const listId = useId();
  const typed = useRef({ text: '', at: 0 });

  const canSearch = searchable ?? options.length > 8;
  const selected = options.find((option) => option.value === value);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((option) => plain(option).toLowerCase().includes(q) || option.value.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const place = useCallback(() => {
    const el = trigger.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - 12;
    const aboveSpace = rect.top - 12;
    const above = below < 220 && aboveSpace > below;
    const maxHeight = Math.max(160, Math.min(POP_MAX, above ? aboveSpace : below));
    const width = Math.max(rect.width, 200);
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    setPos({ top: above ? rect.top - 6 : rect.bottom + 6, left, width, maxHeight, above });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onMove = () => place();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (trigger.current?.contains(target) || pop.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const index = Math.max(0, visible.findIndex((option) => option.value === value));
    setActive(index);
    if (canSearch) setTimeout(() => search.current?.focus(), 0);
    // reset only on open
  }, [open]);

  useEffect(() => {
    pop.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const close = (refocus = true) => {
    setOpen(false);
    setQuery('');
    if (refocus) trigger.current?.focus();
  };

  const choose = (option: SelectOption<T> | undefined) => {
    if (!option || option.disabled) return;
    onChange(option.value);
    close();
  };

  const move = (delta: number) => {
    if (visible.length === 0) return;
    let next = active;
    for (let step = 0; step < visible.length; step += 1) {
      next = (next + delta + visible.length) % visible.length;
      if (!visible[next].disabled) break;
    }
    setActive(next);
  };

  const onKey = (event: KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        move(-1);
        break;
      case 'Home':
        event.preventDefault();
        setActive(0);
        break;
      case 'End':
        event.preventDefault();
        setActive(visible.length - 1);
        break;
      case 'Enter':
        event.preventDefault();
        choose(visible[active]);
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        close(false);
        break;
      default:
        // typeahead
        if (!canSearch && event.key.length === 1) {
          const now = Date.now();
          typed.current = { text: (now - typed.current.at < 600 ? typed.current.text : '') + event.key.toLowerCase(), at: now };
          const hit = visible.findIndex((option) => plain(option).toLowerCase().startsWith(typed.current.text));
          if (hit >= 0) setActive(hit);
        }
    }
  };

  let lastGroup: string | undefined;

  return (
    <>
      <button
        ref={trigger}
        id={id}
        type="button"
        className={['select-trigger', size === 'sm' ? 'sm' : '', open ? 'open' : '', className ?? ''].filter(Boolean).join(' ')}
        style={style}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={onKey}
      >
        {selected?.icon && <span className="select-icon">{selected.icon}</span>}
        <span className={selected ? 'select-value' : 'select-value placeholder'}>{selected ? selected.label : placeholder}</span>
        <Icon name="chevron" size={15} className="select-chevron" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={pop}
            className={pos.above ? 'select-pop above' : 'select-pop'}
            style={{
              top: pos.above ? undefined : pos.top,
              bottom: pos.above ? window.innerHeight - pos.top : undefined,
              left: pos.left,
              minWidth: pos.width,
              maxHeight: pos.maxHeight,
            }}
            onKeyDown={onKey}
          >
            {canSearch && (
              <div className="select-search">
                <Icon name="search" size={14} />
                <input
                  ref={search}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setActive(0);
                  }}
                  placeholder="Cari…"
                  aria-label="Cari pilihan"
                  aria-controls={listId}
                  aria-activedescendant={visible[active] ? `${listId}-${active}` : undefined}
                />
              </div>
            )}
            <div className="select-list" role="listbox" id={listId} aria-label={ariaLabel}>
              {visible.length === 0 && <div className="select-empty">Tidak ada yang cocok</div>}
              {visible.map((option, index) => {
                const header = option.group && option.group !== lastGroup ? option.group : null;
                lastGroup = option.group;
                const isSelected = option.value === value;
                return (
                  <div key={option.value}>
                    {header && <div className="select-group">{header}</div>}
                    <div
                      id={`${listId}-${index}`}
                      data-index={index}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled || undefined}
                      className={['select-option', index === active ? 'active' : '', isSelected ? 'selected' : '', option.disabled ? 'disabled' : '']
                        .filter(Boolean)
                        .join(' ')}
                      onMouseEnter={() => setActive(index)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => choose(option)}
                    >
                      {option.icon && <span className="select-icon">{option.icon}</span>}
                      <span className="select-text">
                        <span className="select-label">{option.label}</span>
                        {option.description && <span className="select-desc">{option.description}</span>}
                      </span>
                      {isSelected && <Icon name="check" size={15} className="select-check" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
